import { Star } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { Search } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Pagination } from "rsuite";
import { AxiosHttpClient } from "../../../../../services/AxiosHttpService";
import { getSpecializationLabelKey } from "../../../../../models/specializationConstants";
import type { Dispatch } from "../../../../../store";
import { getConversationsAction } from "../../../../../store/Conversation/actions";
import { FlexContainer } from "../../../../../styles/FlexContainer";
import { useEnrichedUsersPresence } from "../../../../Reusable/UseEnrichedUsersPresence";
import ExpertVerifiedBadge from "../../../../Reusable/ExpertVerifiedBadge";
import {
  calculateTotalExperienceYears,
  getPresenceMessage,
  getUserFullName,
} from "../../../../Reusable/Utils";
import { BotChatMessage } from "../../../../../models/botChatMessage";

const stripHtml = (html: string): string => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html || "";
  return (tmp.textContent || tmp.innerText || "").trim();
};

export interface RequestCertificationProps {
  popupActive: string;
  setPopupActive: React.Dispatch<React.SetStateAction<string>>;
  messages: BotChatMessage[];
}

const _RequestCertification: React.FC<RequestCertificationProps> = ({
  popupActive,
  setPopupActive,
  messages,
}) => {
  const requestCertification = () => {
    const sharedMessageIds = Array.from(selectedMessageIds);
    if (!sharedMessageIds.includes(popupActive)) sharedMessageIds.push(popupActive);
    AxiosHttpClient.post(
      "api/v1/docuveritas/conversations/messages/certifications/request",
      {
        chatMessageId: popupActive,
        expertId: selectedExpert,
        initialMessage: initialMessage,
        sharedMessageIds: sharedMessageIds,
      }
    )
      .then(() => {
        setPopupActive("");
        setStep(0);
        setSelectedExpert(null);
        setInitialMessage("");
      })
      .catch((err) => {});
  };

  const getExpertsPage = () => {
    if (previousController.current) {
      previousController.current.abort();
    }
    const controller = new AbortController();
    const signal = controller.signal;
    previousController.current = controller;
    let criteriaList = "roles=EXPERT";
    if (searchQuery) {
      criteriaList += `,fullName=${searchQuery}`;
    }
    if (selectedTab === "available") {
      criteriaList += `,lastSeen=${dayjs()
        .subtract(30, "seconds")
        .format("DD/MM/YYYY HH:mm:ss.SSS")}`;
    }
    if (selectedTab === "best") {
      criteriaList += `,averageRating=4=greaterThanEqual`;
    }
    if (selectedTab === "verified") {
      criteriaList += `,expertDetails_verificationStatus=VERIFIED`;
    }
    AxiosHttpClient.get<any>(
      "api/v1/docuveritas/users/page",
      {
        criteriaList: criteriaList,
        pageNumber: page - 1,
        pageSize: limit,
        sort: selectedTab === "best" ? "averageRating" : undefined,
        order: "desc",
      },
      signal
    )
      .then((res) => {
        // Les experts suspendus ne peuvent plus être sollicités : on les masque.
        const visible = (res.content || []).filter(
          (e: any) => e.expertDetails?.verificationStatus !== "SUSPENDED"
        );
        // Priorisation des experts vérifiés dans les résultats de recherche.
        visible.sort(
          (a: any, b: any) =>
            (b.expertDetails?.verificationStatus === "VERIFIED" ? 1 : 0) -
            (a.expertDetails?.verificationStatus === "VERIFIED" ? 1 : 0)
        );
        setExperts(visible);
        setTotalElements(res.totalElements);
      })
      .catch((err) => {});
  };

  const previousController = useRef<AbortController>(null!);

  const limit = 10;

  const [page, setPage] = useState(1);

  const [totalElements, setTotalElements] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [expertCardHovered, setExpertCardHovered] = useState("");
  const [selectedExpert, setSelectedExpert] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState("all");
  const [experts, setExperts] = useEnrichedUsersPresence();

  const [step, setStep] = useState(0);
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(
    new Set()
  );
  const [initialMessage, setInitialMessage] = useState("");

  const toggleMessage = (id: string) => {
    if (id === popupActive) return;
    setSelectedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { t } = useTranslation();

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedTab]);

  useEffect(() => {
    getExpertsPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedTab, page]);

  useEffect(() => {
    if (!popupActive) return;
    setStep(0);
    setSelectedExpert(null);
    setInitialMessage("");
    const ids = new Set<string>();
    const idx = messages.findIndex((m) => m.id === popupActive);
    if (idx === -1) {
      ids.add(popupActive);
    } else {
      const start = Math.max(0, idx - 5);
      for (let i = start; i <= idx; i++) ids.add(messages[i].id);
    }
    setSelectedMessageIds(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupActive]);

  return (
    <Dialog
      open={popupActive !== ""}
      onClose={() => setPopupActive("")}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "8px",
          maxWidth: "800px",
          padding: "24px",
          backgroundColor: "hsl(var(--background))",
          color: "hsl(var(--foreground))",
          border: "1px solid hsl(var(--border))",
        },
      }}
    >
      <FlexContainer flexDirection={"column"} marginBottom={"16px"}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            lineHeight: "1.75rem",
            color: "hsl(var(--foreground))",
          }}
        >
          {t("REQUEST_CERTIFICATION_TITLE")}
        </Typography>

        <Stepper activeStep={step} sx={{ my: 2 }}>
          <Step>
            <StepLabel>{t("REQUEST_CERTIFICATION_STEP_EXPERT")}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t("REQUEST_CERTIFICATION_STEP_MESSAGES")}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t("REQUEST_CERTIFICATION_STEP_COMMENT")}</StepLabel>
          </Step>
        </Stepper>

        {step === 0 && (
          <>
        {/* Search + filter */}
        <Box sx={{ display: "flex", gap: 2, my: "6px" }}>
          <Box
            sx={{
              position: "relative",
              flex: 1,
              display: "flex",
              alignItems: "center",
              borderRadius: "6px",
              p: "10px",
              height: "40px",
              border: "1px solid hsl(var(--border))",
              backgroundColor: "hsl(var(--background))",
            }}
          >
            <Search
              style={{
                height: "16px",
                width: "16px",
                marginRight: "8px",
                color: "hsl(var(--muted-foreground))",
              }}
            />
            <TextField
              variant="standard"
              fullWidth
              placeholder={t("REQUEST_CERTIFICATION_SEARCH_LABEL")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                disableUnderline: true,
              }}
              sx={{
                "& .MuiInputBase-input": {
                  color: "hsl(var(--foreground))",
                  "&::placeholder": {
                    color: "hsl(var(--muted-foreground))",
                    opacity: 1,
                    fontSize: "14px",
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            display: "flex",
            borderRadius: "8px",
            padding: "4px",
            height: "40px",
            width: "fit-content",
            backgroundColor: "hsl(var(--muted))",
          }}
        >
          {[
            { label: t("REQUEST_CERTIFICATION_ALL_TAB"), value: "all" },
            {
              label: t("REQUEST_CERTIFICATION_CONNECTED_TAB"),
              value: "available",
            },
            {
              label: t("REQUEST_CERTIFICATION_VERIFIED_TAB"),
              value: "verified",
            },
            { label: t("REQUEST_CERTIFICATION_BEST_RATED_TAB"), value: "best" },
          ].map((tab) => (
            <Button
              key={tab.value}
              onClick={() => {
                setSelectedTab(tab.value);
              }}
              disableElevation
              sx={{
                textTransform: "none",
                paddingX: "12px",
                paddingY: "6px",
                fontSize: "0.875rem",
                fontWeight: 500,
                borderRadius: "6px",
                transition: "all 0.2s ease-in-out",
                backgroundColor:
                  selectedTab === tab.value
                    ? "hsl(var(--background))"
                    : "transparent",
                color:
                  selectedTab === tab.value
                    ? "hsl(var(--foreground))"
                    : "hsl(var(--muted-foreground))",
                boxShadow:
                  selectedTab === tab.value
                    ? "0 1px 2px 0 rgb(0 0 0 / 0.05)"
                    : "none",
                "&:hover": {
                  backgroundColor:
                    selectedTab === tab.value
                      ? "hsl(var(--background))"
                      : "transparent",
                  color:
                    selectedTab === tab.value
                      ? "hsl(var(--foreground))"
                      : "hsl(var(--foreground))",
                },
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Box>
          </>
        )}
      </FlexContainer>
      <DialogContent sx={{ p: "8px 16px 0 0" }}>
        {step === 0 && (
          <>
        {/* Experts list */}
        {experts.length === 0 ? (
          <Typography
            align="center"
            sx={{
              mt: 8,
              color: "hsl(var(--muted-foreground))",
            }}
          >
            {t("REQUEST_CERTIFICATION_NO_RESULTS")}
          </Typography>
        ) : (
          experts.map((expert) => (
            <Card
              key={expert.id}
              onClick={() => setSelectedExpert(expert.id)}
              sx={{
                mb: 2,
                position: "relative",
                border:
                  selectedExpert === expert.id
                    ? "2px solid hsl(var(--primary))"
                    : "1px solid hsl(var(--border))",
                cursor: "pointer",
                transition: "all 0.2s ease",
                borderRadius: "8px",
                boxShadow: "none",
                backgroundColor: "hsl(var(--card))",
                "&:hover": {
                  borderColor:
                    selectedExpert === expert.id
                      ? "hsl(var(--primary))"
                      : "hsl(var(--muted-foreground))",
                },
              }}
              onMouseEnter={() => setExpertCardHovered(expert.id)}
              onMouseLeave={() => setExpertCardHovered("")}
            >
              {expertCardHovered === expert.id && (
                <IconButton
                  size="medium"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/feature/profile/${expert.id}`, "_blank");
                  }}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "hsl(var(--background))",
                    color: "hsl(var(--muted-foreground))",
                    "&:hover": {
                      backgroundColor: "hsl(var(--muted))",
                    },
                  }}
                  title="View profile"
                >
                  <VisibilityIcon fontSize="medium" />
                </IconButton>
              )}
              <CardContent sx={{ p: "16px" }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: "16px",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <Avatar
                      src={expert.imageUrl}
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        color: "white",
                        border: "1px solid rgba(0, 0, 0, 0.12)",
                      }}
                    />
                    <Box
                      title={getPresenceMessage(expert, t)}
                      sx={{
                        position: "absolute",
                        bottom: 2,
                        right: 2,
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        bgcolor: expert.connected ? "success.main" : "grey.400",
                        border: "2px solid",
                        borderColor: "common.white",
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: "4px",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            fontSize: "18px",
                            lineHeight: "28px",
                            color: "hsl(var(--foreground))",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            {getUserFullName(expert)}
                            <ExpertVerifiedBadge
                              status={expert.expertDetails?.verificationStatus}
                            />
                          </Box>
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "hsl(var(--muted-foreground))",
                          }}
                        >
                          {expert.expertDetails?.specialties
                            .map((s) => t(getSpecializationLabelKey(s)))
                            .join(", ")}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: "8px",
                      }}
                    >
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={`star-${i}`}
                          fontSize="small"
                          sx={{
                            color:
                              i < Math.floor(expert.averageRating ?? 0)
                                ? "#FACC15"
                                : i === Math.floor(expert.averageRating ?? 0) &&
                                  Math.floor(expert.averageRating ?? 0) % 1 >=
                                    0.5
                                ? "#FACC15"
                                : "#E5E7EB",
                            fontSize: "1.1rem",
                          }}
                        />
                      ))}
                      <Typography
                        variant="body2"
                        sx={{
                          ml: 1,
                          color: "hsl(var(--muted-foreground))",
                        }}
                      >
                        {(expert.averageRating ?? 0).toFixed(1)} (
                        {expert.reviewsCount ?? 0})
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: "4px",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: "hsl(var(--foreground))",
                          }}
                        >
                          {t("REQUEST_CERTIFICATION_EXPERIENCE_LABEL") + ":"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "hsl(var(--muted-foreground))",
                          }}
                        >
                          {calculateTotalExperienceYears(
                            expert.expertDetails!.experiences
                          ) + " ans"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: "hsl(var(--foreground))",
                          }}
                        >
                          {t("REQUEST_CERTIFICATION_SEARCH_PRICE") + ":"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "hsl(var(--muted-foreground))",
                          }}
                        >
                          {" Dhs"}
                          /certification
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: "hsl(var(--foreground))",
                          }}
                        >
                          {t("REQUEST_CERTIFICATION_SEARCH_LOCATION") + ":"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "hsl(var(--muted-foreground))",
                          }}
                        >
                          {expert.location?.name +
                            ", " +
                            expert.location?.countryCode}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: "hsl(var(--foreground))",
                          }}
                        >
                          {t(
                            "REQUEST_CERTIFICATION_SEARCH_NBR_CERTIFICATIONS"
                          ) + ":"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "hsl(var(--muted-foreground))",
                          }}
                        >
                          {expert.certificationsCount ?? 0}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        )}

        <Box sx={{ marginTop: "16px" }}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={3}
            size="sm"
            layout={["pager"]}
            total={totalElements}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
          />
        </Box>
          </>
        )}

        {step === 1 && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: "hsl(var(--muted-foreground))" }}
            >
              {t("REQUEST_CERTIFICATION_MESSAGES_HELP")}
            </Typography>
            {messages.map((m) => {
              const isAnchor = m.id === popupActive;
              const checked = selectedMessageIds.has(m.id) || isAnchor;
              const text = stripHtml(m.content);
              return (
                <Box
                  key={m.id}
                  onClick={() => toggleMessage(m.id)}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    p: 1,
                    mb: 1,
                    borderRadius: "6px",
                    cursor: isAnchor ? "default" : "pointer",
                    border: "1px solid hsl(var(--border))",
                    backgroundColor: isAnchor
                      ? "hsl(var(--muted))"
                      : "transparent",
                  }}
                >
                  <Checkbox
                    checked={checked}
                    disabled={isAnchor}
                    size="small"
                    sx={{ p: 0.5 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "hsl(var(--muted-foreground))",
                      }}
                    >
                      {m.fromUser
                        ? t("REQUEST_CERTIFICATION_MSG_USER")
                        : t("REQUEST_CERTIFICATION_MSG_AI")}
                      {isAnchor
                        ? " • " + t("REQUEST_CERTIFICATION_MSG_ANCHOR")
                        : ""}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "hsl(var(--foreground))",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {text.slice(0, 240)}
                      {text.length > 240 ? "…" : ""}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {step === 2 && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: "hsl(var(--muted-foreground))" }}
            >
              {t("REQUEST_CERTIFICATION_COMMENT_HELP")}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder={t("REQUEST_CERTIFICATION_COMMENT_PLACEHOLDER")}
              value={initialMessage}
              onChange={(e) => setInitialMessage(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: "16px 0 0 0" }}>
        <Button
          onClick={() => setPopupActive("")}
          variant="outlined"
          sx={{
            textTransform: "none",
            py: "8px",
            px: "16px",
            borderRadius: "6px",
            borderColor: "hsl(var(--border))",
            color: "hsl(var(--foreground))",
            "&:hover": {
              backgroundColor: "hsl(var(--muted))",
              borderColor: "hsl(var(--border))",
            },
          }}
        >
          {t("REQUEST_CERTIFICATION_CANCEL")}
        </Button>
        {step > 0 && (
          <Button
            onClick={() => setStep(step - 1)}
            variant="outlined"
            sx={{
              textTransform: "none",
              py: "8px",
              px: "16px",
              borderRadius: "6px",
              borderColor: "hsl(var(--border))",
              color: "hsl(var(--foreground))",
              "&:hover": {
                backgroundColor: "hsl(var(--muted))",
                borderColor: "hsl(var(--border))",
              },
            }}
          >
            {t("REQUEST_CERTIFICATION_BACK")}
          </Button>
        )}
        {step < 2 && (
          <Button
            onClick={() => setStep(step + 1)}
            variant="contained"
            disabled={step === 0 && !selectedExpert}
            sx={{
              textTransform: "none",
              py: "8px",
              px: "16px",
              borderRadius: "6px",
              backgroundColor: "hsl(var(--muted-foreground))",
              color: "hsl(var(--background))",
              "&:hover": {
                backgroundColor: "hsl(var(--foreground))",
              },
              "&:disabled": {
                backgroundColor: "hsl(var(--muted))",
                color: "hsl(var(--muted-foreground))",
              },
            }}
          >
            {t("REQUEST_CERTIFICATION_NEXT")}
          </Button>
        )}
        {step === 2 && (
          <Button
            onClick={() => {
              requestCertification();
            }}
            variant="contained"
            sx={{
              textTransform: "none",
              py: "8px",
              px: "16px",
              borderRadius: "6px",
              backgroundColor: "hsl(var(--muted-foreground))",
              color: "hsl(var(--background))",
              "&:hover": {
                backgroundColor: "hsl(var(--foreground))",
              },
            }}
          >
            {t("REQUEST_CERTIFICATION_SUBMIT")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const RequestCertification = connect(null, (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getConversationsAction: getConversationsAction,
    },
    dispatch
  )
)(_RequestCertification);

export default RequestCertification;
